/**
 * SCHNELLWEG FUER DIE ZIELE-ANSICHT (Rafael, 24.10.2026: „wenn ich unten auf den Assistenten klicke, sehe ich oben die gleiche Ansicht … und er muss schnell sein“).
 *
 * Ein kleiner Store im Skriptspeicher (useSyncExternalStore, KEIN localStorage — das L0-Gate wertet einen Speicher-Startwert als stillen Rueckfall): er haelt die
 * Auswahl (Oberthema/Unterziel) und den Statusfilter der Ziele-Ansicht, damit Dock-Knoepfe und Kurzbefehle sie ohne Modell, ohne Server und ohne Netz setzen koennen.
 * Nur lokal im eigenen Fenster; es gibt keine Fernsteuerung. Die Liste (`OberthemenListe`) liest und schreibt ihre Auswahl hier, `GuideHof` meldet die Ansicht und die
 * Ziele zurueck. Jeder Befehl merkt den Stand davor fuer „Rueckgaengig“.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { ALLE_AUSWAHL, parseAnsichtBefehl, type AnsichtArt, type AnsichtWert, type Auswahl, type Status, type ZielEintrag } from '../components/guide-oberthemen';

export interface Vorher { auswahl: Auswahl; filter: Status | null; ansicht: AnsichtArt | null }
export interface SteuerMeldung { nr: number; text: string; /** Der getippte Originaltext (nur bei Kurzbefehlen) — fuer „Als Nachricht senden“. */ original?: string }
export interface SteuerZustand {
  auswahl: Auswahl; filter: Status | null;
  /** Die Ansicht, die GuideHof gerade zeigt (von dort gemeldet; `null` solange nichts gemeldet ist). */
  ansicht: AnsichtArt | null;
  /** Ein Wunsch an GuideHof, die Ansicht zu wechseln; `nr` zaehlt hoch, damit derselbe Wunsch zweimal wirken kann. */
  ansichtWunsch: { nr: number; ansicht: AnsichtArt } | null;
  ziele: ZielEintrag[];
  meldung: SteuerMeldung | null;
  vorher: Vorher | null;
  /** PHASE 2-HAKEN: ein Vorschlag des Assistenten fuer die Reihenfolge in „Heute dran“ (setzt ihn `setzeReihenfolgeVorschlag`; heute ruft das nichts auf). */
  reihenfolgeVorschlag: { nr: number; ids: string[]; begruendung: string } | null;
  /** Ein Text, der zurueck ins Eingabefeld des Assistenten soll; `nr` zaehlt hoch. Wird nie automatisch gesendet. */
  rueckgabe: { nr: number; text: string } | null;
}

const gleichAuswahl = (a: Auswahl, b: Auswahl) => a.gruppe === b.gruppe && a.unterziel === b.unterziel;
const START: SteuerZustand = { auswahl: ALLE_AUSWAHL, filter: null, ansicht: null, ansichtWunsch: null, ziele: [], meldung: null, vorher: null, rueckgabe: null, reihenfolgeVorschlag: null };

export function erzeugeAnsichtSteuerung() {
  let z: SteuerZustand = START;
  let zaehler = 0;
  let gebundenerNutzer: string | null = null;
  const hoerer = new Set<() => void>();
  const setze = (neu: SteuerZustand) => { if (neu === z) return; z = neu; for (const h of [...hoerer]) h(); };
  return {
    lies: (): SteuerZustand => z,
    abonniere(fn: () => void): () => void { hoerer.add(fn); return () => { hoerer.delete(fn); }; },
    /** Eigene Klicks in der Liste: wirken sofort und loeschen eine alte „Rueckgaengig“-Meldung (die waere jetzt falsch). */
    setzeAuswahl(a: Auswahl): void { if (gleichAuswahl(a, z.auswahl)) return; setze({ ...z, auswahl: a, meldung: null, vorher: null }); },
    setzeFilter(f: Status | null | ((alt: Status | null) => Status | null)): void {
      const neu = typeof f === 'function' ? f(z.filter) : f;
      if (neu === z.filter) return;
      setze({ ...z, filter: neu, meldung: null, vorher: null });
    },
    /** GuideHof meldet, was es zeigt; unveraenderte Werte loesen nichts aus. */
    veroeffentliche(teil: { ansicht?: AnsichtArt; ziele?: ZielEintrag[] }): void {
      const ziele = teil.ziele && JSON.stringify(teil.ziele) !== JSON.stringify(z.ziele) ? teil.ziele : z.ziele;
      const ansicht = teil.ansicht ?? z.ansicht;
      if (ziele === z.ziele && ansicht === z.ansicht) return;
      setze({ ...z, ziele, ansicht });
    },
    /**
     * Ein Befehl (Knopf oder Kurzbefehl). `stillWennGleich`: aendert er nichts, gibt es keine Meldung (Klick auf den Assistenten). Rueckgabe: ob etwas geaendert wurde.
     * Der Stand davor wird fuer „Rueckgaengig“ gemerkt; die Ansicht wird nur gewuenscht, wenn sie sich unterscheidet.
     */
    befehl(wert: AnsichtWert, text: string, opt: { stillWennGleich?: boolean; original?: string } = {}): boolean {
      const auswahl = wert.auswahl ?? z.auswahl;
      const filter = wert.filter === undefined ? z.filter : wert.filter;
      const wunsch = wert.ansicht && wert.ansicht !== z.ansicht ? wert.ansicht : null;
      const geaendert = !gleichAuswahl(auswahl, z.auswahl) || filter !== z.filter || wunsch !== null;
      if (!geaendert && opt.stillWennGleich) return false;
      setze({
        ...z, auswahl, filter,
        ansichtWunsch: wunsch ? { nr: (z.ansichtWunsch?.nr ?? 0) + 1, ansicht: wunsch } : z.ansichtWunsch,
        meldung: { nr: ++zaehler, text, ...(opt.original !== undefined ? { original: opt.original } : {}) },
        vorher: geaendert ? { auswahl: z.auswahl, filter: z.filter, ansicht: z.ansicht } : z.vorher,
      });
      return geaendert;
    },
    /** Stellt den Stand vor dem letzten Befehl wieder her (auch die Ansicht, falls der Befehl sie gewechselt hat). */
    rueckgaengig(): boolean {
      const v = z.vorher;
      if (!v) return false;
      const wunsch = v.ansicht && v.ansicht !== z.ansicht ? { nr: (z.ansichtWunsch?.nr ?? 0) + 1, ansicht: v.ansicht } : z.ansichtWunsch;
      setze({ ...z, auswahl: v.auswahl, filter: v.filter, ansichtWunsch: wunsch, meldung: null, vorher: null });
      return true;
    },
    /** Schliesst die Meldung — nur die mit dieser Nummer, damit ein Zeitgeber keine neuere schliesst. */
    meldungSchliessen(nr: number): void { if (z.meldung?.nr === nr) setze({ ...z, meldung: null }); },
    /**
     * Alles, was dem Vorgaenger gehoerte, ist weg: Auswahl, Filter, Meldung, Rueckgabe UND die Oberthemennamen (`ziele`) — die Schnellknoepfe zeigen sonst kurz Namen des
     * Vorgaengers. Nur die Ansicht und der Ansicht-Wunsch bleiben (das ist der Zustand des Fensters, nicht des Nutzers); GuideHof meldet die Ziele des Neuen gleich wieder.
     */
    zuruecksetzen(): void { setze({ ...START, ansicht: z.ansicht, ansichtWunsch: z.ansichtWunsch }); },
    /**
     * PHASE-2-HAKEN (nichts Serverseitiges): so setzt spaeter der Assistent die Reihenfolge von „Heute dran“. Streng: nur Kennungen als Text (hoechstens 20) und eine
     * Begruendung — sonst Fehler, kein stilles Verwerfen. Rafaels eigene Aenderungen fuer heute liegen weiter DARUEBER. `ids` leer nimmt den Vorschlag zurueck.
     */
    setzeReihenfolgeVorschlag(ids: string[], begruendung: string): void {
      if (!Array.isArray(ids) || ids.length > 20 || ids.some(i => typeof i !== 'string' || !i || i.length > 200)) throw new Error('setzeReihenfolgeVorschlag: ids muss eine Liste von hoechstens 20 Buehnen-Kennungen sein');
      if (ids.length > 0 && (typeof begruendung !== 'string' || !begruendung.trim() || begruendung.length > 200)) throw new Error('setzeReihenfolgeVorschlag: eine Begruendung (hoechstens 200 Zeichen) ist Pflicht');
      setze({ ...z, reihenfolgeVorschlag: ids.length ? { nr: (z.reihenfolgeVorschlag?.nr ?? 0) + 1, ids: [...ids], begruendung: begruendung.trim() } : null });
    },
    /** Meldet, wem der Zustand gehoert: bei einer ANDEREN Kennung wird komplett geleert (auch ueber das Aus- und Einhaengen von GuideHof hinweg). Die erste Bindung leert nichts. */
    bindeNutzer(nutzer: string): void {
      const wechsel = gebundenerNutzer !== null && gebundenerNutzer !== nutzer;
      gebundenerNutzer = nutzer;
      if (wechsel) this.zuruecksetzen();
    },
    /**
     * „Als Nachricht senden“: der Kurzbefehl war eine echte Nachricht. Die Ansicht geht zurueck (Rueckgaengig) und der Originaltext kommt zurueck ins Eingabefeld.
     * Er wird ABSICHTLICH nicht automatisch gesendet — ein Fehlklick darf nichts an den Assistenten schicken; ein Enter genuegt.
     */
    alsNachricht(): boolean {
      const original = z.meldung?.original;
      if (original === undefined) return false;
      this.rueckgaengig();
      setze({ ...z, rueckgabe: { nr: (z.rueckgabe?.nr ?? 0) + 1, text: original }, meldung: null });
      return true;
    },
  };
}

export const guideAnsichtSteuerung = erzeugeAnsichtSteuerung();
export type AnsichtSteuerung = ReturnType<typeof erzeugeAnsichtSteuerung>;

export function useGuideAnsichtSteuerung(): SteuerZustand {
  return useSyncExternalStore(guideAnsichtSteuerung.abonniere, guideAnsichtSteuerung.lies, guideAnsichtSteuerung.lies);
}

/**
 * Abfangpunkt fuer das Eingabefeld VOR dem Senden: nur ein eindeutiger Kurzbefehl wird hier verbraucht (true). Alles andere — Satz, mehrdeutig, kein Treffer — gibt
 * false zurueck, und der Text geht unveraendert ans Modell.
 */
export function versucheAnsichtBefehl(text: string, steuerung: AnsichtSteuerung = guideAnsichtSteuerung): boolean {
  const r = parseAnsichtBefehl(text, steuerung.lies().ziele);
  if (r.art !== 'befehl') return false;
  steuerung.befehl(r.wert, r.beschreibung, { original: text });
  return true;
}

/**
 * Die Kopplung der Hofansicht an den Store (ein Aufruf in GuideHof): meldet die gezeigte Ansicht, wechselt sie auf einen Wunsch aus dem Dock (jeder Wunsch wirkt genau
 * einmal; ein Wunsch von VOR dem Einhaengen wird nicht nachgeholt) und setzt bei einem Nutzerwechsel Auswahl, Filter und Meldung zurueck.
 */
export function useAnsichtKopplung(ansicht: AnsichtArt, setAnsicht: (a: AnsichtArt) => void, nutzer: string, steuerung: AnsichtSteuerung = guideAnsichtSteuerung): void {
  useEffect(() => { steuerung.veroeffentliche({ ansicht }); }, [ansicht, steuerung]);
  const wunsch = useSyncExternalStore(steuerung.abonniere, () => steuerung.lies().ansichtWunsch, () => steuerung.lies().ansichtWunsch);
  const letzter = useRef(steuerung.lies().ansichtWunsch?.nr ?? 0);
  useEffect(() => {
    if (!wunsch || wunsch.nr === letzter.current) return;
    letzter.current = wunsch.nr;
    if (wunsch.ansicht !== ansicht) setAnsicht(wunsch.ansicht);
  }, [wunsch]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { steuerung.bindeNutzer(nutzer); }, [nutzer, steuerung]);
}
